import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft } from 'lucide-react';

const PRIVACY_MD = `# Dichiarazione di Protezione dei Dati
## BollettinoX — bollettinox.ch

*Ultimo aggiornamento: giugno 2026*

---

### 1. Titolare del trattamento

Il responsabile del trattamento dei dati personali raccolti tramite questo sito è:

**Luca Genovese**
Lugano, Canton Ticino, Svizzera
Email: info@bollettinox.ch

---

### 2. Quali dati raccogliamo

Raccogliamo i seguenti dati personali:

- **Dati di registrazione:** nome, cognome, indirizzo email, nome azienda
- **Dati di fatturazione:** indirizzo di fatturazione, dati di pagamento (gestiti da Stripe — non conserviamo i dati della carta)
- **Dati d'uso:** progetti, bollettini, documenti caricati o generati tramite la piattaforma
- **Dati tecnici:** indirizzo IP, tipo di browser, log di accesso

---

### 3. Finalità del trattamento

I dati vengono trattati per le seguenti finalità:

- Erogazione del servizio BollettinoX (gestione progetti, fatturazione, bollettini)
- Gestione dell'account utente e autenticazione
- Invio di email transazionali (conferme, ricevute, notifiche di sistema)
- Elaborazione dei pagamenti tramite Stripe
- Assistenza e supporto agli utenti
- Adempimento degli obblighi legali (es. conservazione dati fiscali)

Non utilizziamo i tuoi dati per finalità di marketing senza consenso esplicito.

---

### 4. Base giuridica

Il trattamento si basa su:

- **Esecuzione del contratto:** per erogare il servizio che hai richiesto
- **Obbligo legale:** per la conservazione dei dati fiscali (10 anni secondo il diritto fiscale svizzero)
- **Interesse legittimo:** per la sicurezza della piattaforma e la prevenzione delle frodi

---

### 5. Sub-processori e trasferimenti all'estero

Per erogare il servizio ci avvaliamo di fornitori terzi:

| Fornitore | Finalità | Sede |
|-----------|----------|------|
| **Resend Inc.** | Invio email transazionali | USA |
| **Stripe Inc.** | Elaborazione pagamenti | USA / Irlanda |

Questi fornitori trattano i dati in paesi al di fuori della Svizzera. Il trasferimento avviene sulla base delle clausole contrattuali standard riconosciute dalla Svizzera (nLPD, art. 16).

---

### 6. Conservazione dei dati

I dati vengono conservati per il tempo strettamente necessario:

- **Dati account:** per tutta la durata del rapporto contrattuale + 12 mesi dopo la cancellazione
- **Dati fiscali e di fatturazione:** 10 anni (obbligo legale svizzero)
- **Log tecnici:** 90 giorni

---

### 7. I tuoi diritti

In conformità con la nLPD (nuova Legge federale sulla protezione dei dati, in vigore dal 1° settembre 2023), hai il diritto di:

- **Accesso:** ottenere una copia dei tuoi dati personali
- **Rettifica:** correggere dati inesatti o incompleti
- **Cancellazione:** richiedere la cancellazione dei tuoi dati (salvo obblighi legali)
- **Portabilità:** ricevere i tuoi dati in formato strutturato
- **Opposizione:** opporti a determinati trattamenti

Per esercitare questi diritti, scrivi a: **info@bollettinox.ch**

---

### 8. Cookie e dati tecnici

BollettinoX utilizza esclusivamente cookie tecnici necessari al funzionamento del servizio (es. autenticazione). Non utilizziamo cookie di profilazione o di marketing. Non è richiesto il tuo consenso per questi cookie.

---

### 9. Sicurezza

Adottiamo misure tecniche e organizzative adeguate per proteggere i tuoi dati da accessi non autorizzati, perdita o divulgazione.

---

### 10. Modifiche

Ci riserviamo il diritto di aggiornare questa informativa. In caso di modifiche sostanziali, ne daremo comunicazione via email o tramite avviso sulla piattaforma.

---

### 11. Contatti

Per qualsiasi domanda relativa alla protezione dei dati:

**Luca Genovese**
Email: info@bollettinox.ch
Sito: bollettinox.ch

---

*Questa dichiarazione è conforme alla nuova Legge federale sulla protezione dei dati (nLPD) in vigore in Svizzera dal 1° settembre 2023.*
`;

export const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto bg-white shadow-sm rounded-xl border border-slate-200 p-6 sm:p-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna al login
        </Link>
        <article className="text-slate-700">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="text-3xl font-semibold text-slate-900 mt-0 mb-2" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-base font-medium text-slate-600 mt-0 mb-6" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-3" {...props} />,
              p: ({ node, ...props }) => <p className="text-sm leading-relaxed mb-3" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-sm" {...props} />,
              li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
              hr: ({ node, ...props }) => <hr className="my-6 border-slate-200" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
              em: ({ node, ...props }) => <em className="text-slate-500" {...props} />,
              a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" {...props} />,
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm border-collapse border border-slate-200" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
              th: ({ node, ...props }) => <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-900" {...props} />,
              td: ({ node, ...props }) => <td className="border border-slate-200 px-3 py-2" {...props} />,
            }}
          >
            {PRIVACY_MD}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPage;
